import { Button } from '@base-ui/react';
import type { Theme } from '@emotion/react';
import styled from '@emotion/styled';

import { DISABLED_EXCLUSIONS, DISABLED_SELECTORS, focusRing } from '../../interaction-states';
import { forHover } from '../../responsive';
import { typographyVariantStyles } from '../../typography-variants';
import type { TextButtonColor, TextButtonSize } from './types';

const SIZE: Record<
  TextButtonSize,
  { iconSize: number; gap: keyof Theme['spacing']; typography: 'body3' | 'body2' }
> = {
  sm: { iconSize: 16, gap: 4, typography: 'body3' },
  lg: { iconSize: 20, gap: 8, typography: 'body2' },
};

const PALETTE: Record<
  TextButtonColor,
  (theme: Theme) => { rest: string; hover: string; active: string }
> = {
  brand: (theme) => ({
    rest: theme.colors.brand.text,
    hover: theme.colors.brand.hover,
    active: theme.colors.brand.active,
  }),
  neutral: (theme) => ({
    rest: theme.colors.text.tertiary,
    hover: theme.colors.text.secondary,
    active: theme.colors.text.primary,
  }),
};

export const StyledTextButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== '$color' && prop !== '$size',
})<{ $color: TextButtonColor; $size: TextButtonSize }>(({ theme, $color, $size }) => {
  const palette = PALETTE[$color](theme);
  const { iconSize, gap, typography } = SIZE[$size];

  return [
    {
      display: 'inline-flex',
      alignItems: 'center',
      gap: theme.spacing[gap],
      padding: theme.spacing[0],
      border: 'none',
      outline: 'none',
      background: 'none',
      borderRadius: theme.radius[8],
      boxSizing: 'border-box' as const,
      whiteSpace: 'nowrap' as const,
      textDecoration: 'none',
      cursor: 'pointer',
      WebkitTapHighlightColor: 'transparent',
      transition: theme.motion.hover,
      ...typographyVariantStyles[typography],
      fontWeight: 500,
      color: palette.rest,

      svg: {
        width: iconSize,
        height: iconSize,
        flexShrink: 0,
      },
    },
    {
      [forHover]: {
        [`&:hover${DISABLED_EXCLUSIONS}`]: { color: palette.hover },
      },
      [`&:active${DISABLED_EXCLUSIONS}`]: { color: palette.active },
    },
    focusRing(theme, { inset: false }),
    {
      [DISABLED_SELECTORS]: {
        color: theme.colors.text.disabled,
        cursor: 'not-allowed',

        svg: {
          color: theme.colors.icon.disabled,
        },
      },
    },
  ];
});
